import { AllGroupsHeader } from './FilePickerGroupsView.locstring.json';
import { favoritesHeaderText } from 'owa-locstrings/lib/strings/favoritesheadertext.locstring.json';
import loc from 'owa-localize';
import GroupView from './GroupView';
import getAriaLabelForFileProviderItem from '../utils/getAriaLabelForFileProviderItem';
import { observer } from 'mobx-react-lite';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { ISelection, Selection, SelectionMode, SelectionZone } from '@fluentui/react/lib/Selection';
import { formatUserDate } from 'owa-datetime';
import FileProviderItemViewState, {
    GroupCategory,
    GroupItemViewState,
} from '../../store/schema/FileProviderItemViewState';
import * as React from 'react';
import type { IObjectWithKey } from '@fluentui/react/lib';

const styles = require('./FilePickerGroupsView.scss');

export interface FilePickerGroupsViewProps {
    dataSourceId: string;
    items: FileProviderItemViewState[];
    selectedItems: FileProviderItemViewState[];
    onLoadMoreRows: () => void;
    isLoadRowsInProgress: boolean;
    getCanLoadMore: () => boolean;
    onItemSelected?: (item: IObjectWithKey) => void;
    onItemsSelectionUpdated?: (selectedItems: FileProviderItemViewState[]) => void;
}

export default observer(function FilePickerGroupsView(props: FilePickerGroupsViewProps) {
    //  keeps track of whether the component is mounted or not
    const hasMounted = React.useRef<boolean>();

    React.useEffect(() => {
        selection.setItems(props.items, true /* shouldClear */);
    }, [props.items]); // Only re-run the effect if count changes

    React.useEffect(() => {
        resetSelectedItems(props.selectedItems, selection);
    }, [props.selectedItems]); // Only re-run the effect if count changes

    // When the component mounts we need to set the items and the selected items
    // so that if the user switches between the view the selection is still maintained
    React.useEffect(() => {
        selection.setItems(props.items, true /* shouldClear */);
        hasMounted.current = true;

        return () => {
            hasMounted.current = false;
        };
    }, []);

    /* Use to create a key corresponding to an item when it is selected */
    const getKeyForItem = (item: any, index: number): string => {
        return index.toString();
    };

    const getSecondaryText = (item: GroupItemViewState): string => {
        // LOC_BUG
        return item.joinDate ? 'Joined ' + formatUserDate(item.joinDate) : null;
    };

    /* Called when the user selects items in the file picker */
    const onItemsSelectionChanged = () => {
        const selectedItems = selection.getSelection() as FileProviderItemViewState[];

        if (props.onItemsSelectionUpdated) {
            props.onItemsSelectionUpdated(selectedItems);
        }
    };

    const [selection] = React.useState(
        new Selection({
            onSelectionChanged: onItemsSelectionChanged,
            getKey: getKeyForItem,
        })
    );

    const resetSelectedItems = (
        selectedItems: FileProviderItemViewState[],
        selection: ISelection
    ) => {
        // We need to disable the trigger of change event so during the process of resetting the selection
        selection.setChangeEvents(false);

        // Setting all items to unselected state
        selection.setAllSelected(false);

        // Setting the anchor to the first item
        selection.setIndexSelected(0, false /* isSelected */, true /* shouldAnchor */);

        // Getting the indexes of selected items and then setting selection to true for them
        selectedItems
            .map(selectedItem => props.items.indexOf(selectedItem))
            .forEach(selectedItemIndex =>
                selection.setIndexSelected(
                    selectedItemIndex,
                    true /* isSelected */,
                    false /* shouldAnchor */
                )
            );

        // We enable the change events when we are done
        selection.setChangeEvents(true);
    };

    const createItemView = (itemId: string, itemIndex: number): JSX.Element => {
        const item = props.items[itemIndex] as GroupItemViewState;
        const isSelected = selection.isIndexSelected(itemIndex);

        const itemAriaLabel = getAriaLabelForFileProviderItem(item);
        const secondaryText = getSecondaryText(item);

        return (
            <GroupView
                key={itemId}
                itemIndex={itemIndex}
                isSelected={isSelected}
                primaryText={item.name}
                secondaryText={secondaryText}
                ariaLabel={itemAriaLabel}
                groupSmtpEmailAddress={item.pagingMetadata?.GroupSmtpAddress}
            />
        );
    };

    const favoriteItemIds = props.items
        .filter(item => (item as GroupItemViewState).groupCategory == GroupCategory.Favorite)
        .map(item => item.key);
    const allItemIds = props.items.map(item => item.key);

    const showFavorites = favoriteItemIds.length > 0;
    const showAllGroups = allItemIds.length > 0;

    return (
        <div className={styles.outer}>
            {showFavorites && (
                <div className={styles.container}>
                    <div className={styles.subHeader}>{loc(favoritesHeaderText)}</div>
                    <FocusZone className={styles.focusZone}>
                        <SelectionZone
                            selection={selection}
                            selectionMode={SelectionMode.single}
                            onItemInvoked={props.onItemSelected}>
                            <div>
                                {favoriteItemIds.map((id, index) => createItemView(id, index))}
                            </div>
                        </SelectionZone>
                    </FocusZone>
                </div>
            )}
            {showAllGroups && (
                <div className={styles.container}>
                    <div className={styles.subHeader}>{loc(AllGroupsHeader)}</div>
                    <FocusZone className={styles.focusZone}>
                        <SelectionZone
                            selection={selection}
                            selectionMode={SelectionMode.single}
                            onItemInvoked={props.onItemSelected}>
                            <div>{allItemIds.map((id, index) => createItemView(id, index))}</div>
                        </SelectionZone>
                    </FocusZone>
                </div>
            )}
        </div>
    );
});
